import { useState } from 'react';
import { Code2, Copy, Check, FileDown } from 'lucide-react';
import { Card, Button, useToast } from '@afrilink/ui';
import { DEVELOPER_TAG_PROMPT } from '@afrilink/config';

/**
 * DeveloperTagCard — the sub-footer credit strip prompt.
 *
 * Rendered in TWO places (owner direction 2026-08-30): /sales beside
 * BankConfirmationLetterCard, and the admin dashboard. It lives in shared/
 * rather than apps/sales/ for exactly that reason — a component imported
 * across an app boundary is how those boundaries stop meaning anything.
 *
 * WHY: every website SightHost delivers carries the same credit strip — the
 * client's registered name and CIPC number on the left, "Developed by
 * SightHost (Pty) Ltd" on the right, nofollow. Getting it right is not a taste
 * question: the left half is a Companies Act §32(4) disclosure, and the
 * nofollow is what stops a sitewide footer link across many client domains
 * reading as a link scheme. Improvised per site, it would drift on both.
 *
 * Copy is the primary action, not download — the prompt is pasted into a
 * coding agent, not filed. The .md download exists for the case where it has
 * to travel to someone else's machine.
 *
 * UNGATED, on the same reasoning as BankConfirmationLetterCard: the text is a
 * build constant, identical for every viewer. It reads nothing from the
 * database, so there is no row to leak and nothing to scope.
 *
 * ACCESS: /sales is behind SalesRoute (sales staff + admin), which a plain
 * `staff` developer cannot reach — and they are who the prompt is for. That
 * gap is why it is on the admin dashboard as well. Takes no props, so a third
 * placement costs one import.
 */
export function DeveloperTagCard() {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(DEVELOPER_TAG_PROMPT);
      setCopied(true);
      setTimeout(() => setCopied(false), 4000);
    } catch {
      toast({
        tone: 'warning',
        title: 'Could not copy',
        body: 'Your browser blocked clipboard access. Use Download instead.',
      });
    }
  }

  function download() {
    // Blob + object URL rather than a data: URI — Safari truncates long data
    // URIs on download, and this prompt is ~5KB.
    const blob = new Blob([DEVELOPER_TAG_PROMPT], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sighthost-sub-footer-prompt.md';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <Card className="p-5">
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"
        >
          <Code2 className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-foreground">Developer tag</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            The sub-footer every SightHost-built site carries — the client&apos;s registered
            name and company number on the left, <span className="whitespace-nowrap">&ldquo;Developed by
            SightHost (Pty) Ltd&rdquo;</span> on the right. Paste this into the coding agent
            building the site.
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            <Button type="button" onClick={copy} disabled={copied}>
              {copied ? (
                <>
                  <Check className="mr-2 h-4 w-4" aria-hidden="true" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="mr-2 h-4 w-4" aria-hidden="true" />
                  Copy prompt
                </>
              )}
            </Button>
            <Button type="button" variant="outline" onClick={download}>
              <FileDown className="mr-2 h-4 w-4" aria-hidden="true" />
              Download .md
            </Button>
          </div>

          <p className="mt-3 text-xs text-muted-foreground">
            Do not edit the rules before pasting. The{' '}
            <code className="rounded bg-muted px-1 py-0.5 text-[11px]">nofollow</code> and the
            registration number are legal and SEO requirements, not preferences.
          </p>
        </div>
      </div>
    </Card>
  );
}
